import { useEffect, useState } from "react";
import { Icon } from "../lib/icons";
import { Button, cn } from "./ui";

// First-visit walkthrough: four short cards on the features people don't discover on their own.
// Shown once per browser (localStorage); Настройки → «Показать подсказки» reopens it.

const KEY = "wa_tour_v1";
const OPEN_EVENT = "wa:tour";

const STEPS: Array<{ icon: string; title: string; text: string }> = [
  {
    icon: "overview",
    title: "Навигация и поиск",
    text: "В меню (слева, на телефоне — ☰) — все отчёты: текущий остаток, по дням, неделям и месяцам. Поиск сверху (или Ctrl+K) сразу находит товар, отчёт или сохранённый вид.",
  },
  {
    icon: "reports",
    title: "Сортировка и фильтры",
    text: "Клик по заголовку столбца — сортировка (Shift+клик — по нескольким). Воронка в заголовке — фильтр столбца, булавка — закрепить столбец.",
  },
  {
    icon: "star",
    title: "Сохранённые виды",
    text: "Настройте столбцы, фильтры и период, затем сохраните через кнопку «Виды». Закреплённый вид появится в меню слева.",
  },
  {
    icon: "download",
    title: "Выгрузка в Excel",
    text: "Кнопка «Скачать» на странице отчёта выгружает то, что вы видите. На странице «Все отчёты» — несколько отчётов сразу.",
  },
];

export function startTour(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function Tour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setOpen(true);
    } catch {
      /* storage blocked — just don't auto-open */
    }
    const h = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (!open) return null;
  const s = STEPS[step];
  const last = step === STEPS.length - 1;
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/30 p-4 backdrop-blur-sm animate-fade-in print:hidden" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Подсказки по сайту"
        className="w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-pop animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <span className="grid h-11 w-11 flex-none place-items-center rounded-lg bg-accent-weak text-accent">
            <Icon name={s.icon} size={20} />
          </span>
          <div className="min-w-0">
            <div className="text-xs font-medium text-text-2">
              Подсказка {step + 1} из {STEPS.length}
            </div>
            <h4 className="mt-0.5 text-md font-semibold">{s.title}</h4>
            <p className="mt-1.5 text-base leading-relaxed text-text-2">{s.text}</p>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Подсказка ${i + 1}`}
                onClick={() => setStep(i)}
                className={cn("h-1.5 rounded-full transition-all", i === step ? "w-5 bg-accent" : "w-1.5 bg-border-strong")}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={close}>
              Пропустить
            </Button>
            {step > 0 && <Button onClick={() => setStep(step - 1)}>Назад</Button>}
            <Button variant="primary" onClick={() => (last ? close() : setStep(step + 1))}>
              {last ? "Понятно" : "Далее"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
